import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { motion, AnimatePresence } from "framer-motion";
import { CoeService } from "../../../services/dataService";

/* ─── Icons ─── */
const UploadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);
const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" />
  </svg>
);
const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18m-2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);
const LockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const UnlockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 9.9-1" />
  </svg>
);
const DotsVert = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" />
  </svg>
);
const FileIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
  </svg>
);
const XIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const TYPE_CONFIG = {
  fdp_content:     { label: "FDP Content",     color: "blue"   },
  lab_content:     { label: "Lab Content",     color: "emerald" },
  project_content: { label: "Project Content", color: "violet"  },
};
const COLOR = {
  blue:    { bg: "bg-blue-50",    text: "text-blue-600",    border: "border-blue-200",    badge: "bg-blue-100 text-blue-700" },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-600", border: "border-emerald-200", badge: "bg-emerald-100 text-emerald-700" },
  violet:  { bg: "bg-violet-50",  text: "text-violet-600",  border: "border-violet-200",  badge: "bg-violet-100 text-violet-700" },
};

/* ─── Portal Dropdown ─── */
const TailDropdown = ({ open, onClose, children, className = "", triggerElement }) => {
  const ref = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open, onClose]);
  useEffect(() => {
    if (open && triggerElement) {
      const r = triggerElement.getBoundingClientRect();
      const sx = window.scrollX || window.pageXOffset, sy = window.scrollY || window.pageYOffset;
      if (r.left > window.innerWidth / 2) setCoords({ top: r.bottom + sy, right: window.innerWidth - (r.right + sx), left: "auto" });
      else setCoords({ top: r.bottom + sy, left: r.left + sx, right: "auto" });
    }
  }, [open, triggerElement]);
  if (!open) return null;
  return createPortal(
    <motion.div ref={ref} initial={{ opacity: 0, scale: 0.95, y: -6 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.12 }}
      style={{ position: "absolute", top: (coords.top || 0) + 5, left: coords.left, right: coords.right, zIndex: 99999, minWidth: "160px" }}
      className={`bg-white rounded-xl border border-slate-100 shadow-2xl overflow-hidden ${className}`}
      onClick={e => e.stopPropagation()}>
      {children}
    </motion.div>, document.body
  );
};

const DropItem = ({ icon, label, color = "text-slate-600", hoverBg = "hover:bg-slate-50", onClick }) => (
  <button onClick={onClick} className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-left text-[9px] font-bold uppercase tracking-wider ${color} ${hoverBg} transition-colors border-0 bg-transparent`}>
    <span className="flex-shrink-0 flex items-center justify-center w-4 h-4">{icon}</span>{label}
  </button>
);

/* ─── Main ─── */
const CoeAdminDocuments = () => {
  const { academyId, domainId, docType } = useParams();
  const navigate = useNavigate();
  const cfg = TYPE_CONFIG[docType] || TYPE_CONFIG.fdp_content;
  const ac = COLOR[cfg.color];

  const [corporateName, setCorporateName] = useState("Corporate");
  const [domainName, setDomainName] = useState("Domain");
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [docTitle, setDocTitle] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [accessMap, setAccessMap] = useState({});
  const [actionOpen, setActionOpen] = useState({});
  const [menuTriggerElMap, setMenuTriggerElMap] = useState({});

  useEffect(() => { fetchData(); }, [academyId, domainId, docType]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [corpRes, domainsRes, docsRes] = await Promise.allSettled([
        CoeService.getActiveAcademies(),
        CoeService.getDomainsByCorporate(academyId),
        CoeService.getDocumentsByDomain(domainId, academyId, docType),
      ]);
      if (corpRes.status === "fulfilled" && corpRes.value?.data) {
        const corp = corpRes.value.data.find(c => String(c.academy_id) === String(academyId));
        if (corp) setCorporateName(corp.academy_brand || corp.academy_name || "Corporate");
      }
      if (domainsRes.status === "fulfilled" && domainsRes.value?.data) {
        const dom = domainsRes.value.data.find(d => String(d.domain_id) === String(domainId));
        if (dom) setDomainName(dom.domain_name || "Domain");
      }
      if (docsRes.status === "fulfilled" && docsRes.value?.data) {
        const docs = Array.isArray(docsRes.value.data) ? docsRes.value.data : [];
        setDocuments(docs);
        const init = {};
        docs.forEach(d => { init[d.coe_id] = d.is_active !== false; });
        setAccessMap(prev => ({ ...init, ...prev }));
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleUpload = async () => {
    if (!selectedFile) return alert("Please select a file.");
    if (!docTitle.trim()) return alert("Please enter a document title.");
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("academy_id", academyId);
    formData.append("domain_id", domainId);
    formData.append("domain_name", domainName);
    formData.append("doc_type", docType);
    formData.append("doc_title", docTitle.trim());
    setSubmitting(true);
    try {
      await CoeService.uploadDocument(formData);
      setUploadOpen(false); setSelectedFile(null); setDocTitle("");
      fetchData();
    } catch (e) { alert("Upload failed: " + (e.response?.data?.detail || e.message)); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (doc) => {
    closeAction(doc.coe_id);
    if (!window.confirm(`Delete "${doc.doc_title || "this document"}"?`)) return;
    try { await CoeService.deleteDocument(doc.coe_id); fetchData(); }
    catch { alert("Failed to delete document."); }
  };

  const handleToggleAccess = async (doc) => {
    const cur = accessMap[doc.coe_id] !== undefined ? accessMap[doc.coe_id] : (doc.is_active !== false);
    const next = !cur;
    setAccessMap(prev => ({ ...prev, [doc.coe_id]: next }));
    closeAction(doc.coe_id);
    try { await CoeService.updateDocument(doc.coe_id, { is_active: next }); }
    catch { setAccessMap(prev => ({ ...prev, [doc.coe_id]: cur })); }
  };

  const toggleAction = (key, el) => {
    setMenuTriggerElMap(prev => ({ ...prev, [key]: el }));
    setActionOpen(prev => ({ ...prev, [key]: !prev[key] }));
  };
  const closeAction = (key) => setActionOpen(prev => ({ ...prev, [key]: false }));

  return (
    <div className="min-h-screen bg-slate-50 font-sans p-6">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Tooltip title="Back">
              <IconButton onClick={() => navigate(`/admin-coe/${academyId}/${domainId}`)} sx={{ bgcolor: "white", border: "1px solid #e2e8f0" }}>
                <ArrowBackIcon sx={{ fontSize: 18, color: "#2563eb" }} />
              </IconButton>
            </Tooltip>
            <div>
              <div className="flex items-center gap-2 text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-1">
                <span>{corporateName}</span><span>/</span><span>{domainName}</span><span>/</span>
                <span className={ac.text}>{cfg.label}</span>
              </div>
              <h1 className="text-2xl font-extrabold text-slate-800 tracking-tight leading-none">{cfg.label}</h1>
            </div>
          </div>
          <button
            onClick={() => { setUploadOpen(true); setSelectedFile(null); setDocTitle(""); }}
            className={`flex items-center gap-2 ${ac.bg} ${ac.text} border ${ac.border} px-5 py-3 rounded-xl font-bold text-[11px] uppercase tracking-widest transition-all hover:shadow-sm`}
          >
            <UploadIcon /> Upload Document
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <CircularProgress size={32} thickness={5} sx={{ color: "#2563eb" }} />
          </div>
        ) : documents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3 bg-white border border-dashed border-slate-200 rounded-3xl">
            <div className={`w-16 h-16 ${ac.bg} border ${ac.border} rounded-2xl flex items-center justify-center ${ac.text}`}><FileIcon /></div>
            <h3 className="text-lg font-black text-slate-600">No documents yet</h3>
            <p className="text-slate-400 text-sm text-center max-w-xs">Upload your first {cfg.label} document for this domain.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="divide-y divide-slate-100">
              {documents.map((doc, idx) => {
                const isOn = accessMap[doc.coe_id] !== undefined ? accessMap[doc.coe_id] : (doc.is_active !== false);
                return (
                  <div key={doc.coe_id} className="flex items-center gap-4 px-6 py-4 hover:bg-slate-50/50 transition-all">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${ac.bg} ${ac.text} border ${ac.border} flex-shrink-0`}>
                      <FileIcon />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[14px] font-bold text-slate-800 truncate">{doc.doc_title || `Document ${idx + 1}`}</p>
                      <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mt-0.5">
                        {cfg.label} • {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : "—"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <IconButton onClick={() => setViewingDoc(doc)} size="small" sx={{ color: "#94a3b8", "&:hover": { color: "#2563eb" } }}>
                        <EyeIcon />
                      </IconButton>
                      {/* 3-dot menu */}
                      <div className="relative">
                        <IconButton onClick={(e) => toggleAction(doc.coe_id, e.currentTarget)} size="small" sx={{ color: "#94a3b8", "&:hover": { color: "#334155" } }}>
                          <DotsVert />
                        </IconButton>
                        <TailDropdown open={!!actionOpen[doc.coe_id]} onClose={() => closeAction(doc.coe_id)} triggerElement={menuTriggerElMap[doc.coe_id]} className="w-52">
                          <div className="py-1.5">
                            <div className="px-3.5 pt-2 pb-1">
                              <p className="text-[8px] font-bold uppercase tracking-widest text-slate-400">Access Control</p>
                            </div>
                            <button
                              onClick={() => handleToggleAccess(doc)}
                              className={`w-full flex items-center justify-between gap-2 px-3.5 py-2.5 text-left transition-colors border-0 ${isOn ? 'hover:bg-emerald-50' : 'hover:bg-red-50'}`}
                            >
                              <span className={`flex items-center gap-2 text-[9px] font-bold uppercase tracking-wider ${isOn ? 'text-emerald-600' : 'text-red-500'}`}>
                                <span className="flex-shrink-0">{isOn ? <UnlockIcon /> : <LockIcon />}</span>
                                {isOn ? 'SPOC & Educator: ON' : 'SPOC & Educator: OFF'}
                              </span>
                              <span className={`relative inline-flex h-4 w-7 flex-shrink-0 rounded-full transition-colors duration-200 ${isOn ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                                <span className={`inline-block h-3 w-3 mt-0.5 rounded-full bg-white shadow transform transition-transform duration-200 ${isOn ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
                              </span>
                            </button>
                            <div className="border-t border-slate-100 my-1" />
                            <DropItem icon={<TrashIcon />} label="Delete" color="text-red-500" hoverBg="hover:bg-red-50" onClick={() => handleDelete(doc)} />
                          </div>
                        </TailDropdown>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </motion.div>

      {/* Document Viewer */}
      <AnimatePresence>
        {viewingDoc && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setViewingDoc(null)} />
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="relative w-full h-full max-w-5xl max-h-[90vh] bg-white rounded-2xl overflow-hidden shadow-2xl flex flex-col">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                <p className="font-bold text-slate-800 text-sm truncate">{viewingDoc.doc_title}</p>
                <button onClick={() => setViewingDoc(null)} className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-all"><XIcon /></button>
              </div>
              <div className="flex-1 overflow-hidden">
                <iframe src={viewingDoc.doc_link?.replace("/view", "/preview")} className="w-full h-full border-0" allow="autoplay" />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Upload Modal */}
      <AnimatePresence>
        {uploadOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm" onClick={() => !submitting && setUploadOpen(false)} />
            <motion.div initial={{ opacity: 0, scale: 0.95, y: -8 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className={`h-1 w-full ${ac.bg.replace('bg-', 'bg-gradient-to-r from-') || 'bg-blue-500'}`} style={{ background: cfg.color === 'blue' ? 'linear-gradient(to right, #3b82f6, #2563eb)' : cfg.color === 'emerald' ? 'linear-gradient(to right, #10b981, #059669)' : 'linear-gradient(to right, #8b5cf6, #7c3aed)' }} />
              <div className="flex items-center justify-between px-6 pt-5 pb-2">
                <h2 className="text-[15px] font-extrabold text-slate-800">Upload {cfg.label}</h2>
                <button onClick={() => setUploadOpen(false)} className="w-7 h-7 rounded-lg bg-slate-100 text-slate-400 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-all"><XIcon /></button>
              </div>
              <div className="px-6 pb-6 pt-3 space-y-4">
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Document Title</label>
                  <input type="text" value={docTitle} onChange={e => setDocTitle(e.target.value)} placeholder="e.g. Lab Manual Unit 1" className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-blue-100 outline-none transition" />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">File</label>
                  <div
                    onClick={() => document.getElementById("doc-file-input").click()}
                    className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${selectedFile ? `${ac.border} ${ac.bg}` : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`}
                  >
                    <input id="doc-file-input" type="file" accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.zip,.rar,.txt" className="hidden" onChange={e => setSelectedFile(e.target.files[0])} />
                    {selectedFile ? (
                      <div>
                        <p className={`font-bold text-sm ${ac.text}`}>{selectedFile.name}</p>
                        <p className="text-[11px] text-slate-400 mt-1">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm font-semibold text-slate-400">Click to select file</p>
                        <p className="text-[11px] text-slate-300 mt-1">PDF, Word, PPT, Excel, ZIP</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 pt-1">
                  <button onClick={() => setUploadOpen(false)} disabled={submitting} className="flex-1 py-2.5 rounded-xl font-bold text-[11px] uppercase tracking-wider bg-slate-100 text-slate-500 hover:bg-slate-200 transition-all">Cancel</button>
                  <button
                    onClick={handleUpload}
                    disabled={submitting || !selectedFile || !docTitle.trim()}
                    className={`flex-1 py-2.5 rounded-xl font-bold text-[11px] uppercase tracking-wider text-white transition-all ${submitting || !selectedFile || !docTitle.trim() ? 'bg-slate-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 shadow-md'}`}
                  >
                    {submitting ? "Uploading..." : "Upload"}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CoeAdminDocuments;
